'use client';
import { createContext, useContext, ReactNode, useRef, useState, useMemo, useEffect } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import { getApplicationsDocumentsQuery } from '../queries/getApplicationsDocumentsQuery';
import Documents from '../types/Documents';
import DocumentPage from '../types/DocumentPage';

interface ApplicationsDocumentsContextValue {
    rootDocument: DocumentPage | null | undefined;
    loading: boolean;
    error: any;
    findLocalizedDocumentById: (id: string) => DocumentPage | undefined;
}

const ApplicationsDocumentsContext = createContext<ApplicationsDocumentsContextValue | undefined>(
    undefined
);

interface ApplicationsDocumentsProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ApplicationsDocumentsProvider = ({
    children,
    apolloClient,
}: ApplicationsDocumentsProviderProps) => {
    const { locale } = useLocale();
    const queryDoc = useMemo(() => getApplicationsDocumentsQuery(locale), [locale]);
    const [rootDocument, setRootDocument] = useState<DocumentPage | null>(null);
    const previousRootDocumentRef = useRef<DocumentPage | null>(null);
    const { loading, error, data } = useQuery<Documents>(queryDoc, {
        client: apolloClient,
    });

    useEffect(() => {
        if (data?.getDocument) {
            setRootDocument((currentRootDocument) => {
                previousRootDocumentRef.current = currentRootDocument;
                return data.getDocument;
            });
        }
    }, [data]);

    useEffect(() => {
        if (error) {
            console.error('ApplicationsDocumentsProvider query error', error);
        }
    }, [error]);

    const findLocalizedDocumentById = (id: string): DocumentPage | undefined => {
        let document = getDocumentById(rootDocument, id);
        if (!document) {
            document = findDocumentByTranslation(rootDocument, id);
        }

        if (!document && previousRootDocumentRef.current) {
            let parentDocument = getDocumentById(previousRootDocumentRef.current, id);
            while (parentDocument && !document) {
                document = findDocumentByTranslation(rootDocument, parentDocument.id);
                if (!document) {
                    parentDocument = getDocumentParent(
                        previousRootDocumentRef.current,
                        parentDocument.id
                    );
                }
            }
        }

        return document;
    };

    return (
        <ApplicationsDocumentsContext.Provider
            value={{
                rootDocument,
                loading,
                error,
                findLocalizedDocumentById,
            }}
        >
            {children}
        </ApplicationsDocumentsContext.Provider>
    );
};

function useApplicationsDocuments() {
    const context = useContext(ApplicationsDocumentsContext);
    if (!context) {
        throw new Error(
            'useApplicationsDocuments must be used within an ApplicationsDocumentsProvider'
        );
    }
    return context;
}

// Utility functions
function getDocumentById(root: DocumentPage | null, id: string): DocumentPage | undefined {
    if (!root) {
        return undefined;
    }

    const findInTree = (doc: DocumentPage): DocumentPage | undefined => {
        if (doc.id === id) {
            return doc;
        }
        if (doc.children) {
            for (const child of doc.children) {
                const foundChild = findInTree(child);
                if (foundChild) return foundChild;
            }
        }
        return undefined;
    };

    return findInTree(root);
}

function findDocumentByTranslation(
    root: DocumentPage | null,
    translationDocumentId: string
): DocumentPage | undefined {
    if (!root) {
        return undefined;
    }

    const findInTree = (doc: DocumentPage): DocumentPage | undefined => {
        if (doc.translations) {
            for (const translation of doc.translations) {
                if (translation.id === parseInt(translationDocumentId, 10)) {
                    return doc;
                }
            }
        }
        if (doc.children) {
            for (const child of doc.children) {
                const foundChild = findInTree(child);
                if (foundChild) return foundChild;
            }
        }
        return undefined;
    };

    return findInTree(root);
}

function getDocumentParent(root: DocumentPage | null, id: string): DocumentPage | undefined {
    if (!root) {
        return undefined;
    }

    const findInTree = (doc: DocumentPage): DocumentPage | undefined => {
        if (doc.children) {
            for (const child of doc.children) {
                if (child.id === id) {
                    return doc;
                }
                const foundParent = findInTree(child);
                if (foundParent) return foundParent;
            }
        }
        return undefined;
    };

    return findInTree(root);
}

function buildDocumentRoute(document: DocumentPage): string {
    const [, lang, ...pathParts] = document.fullpath.split('/');
    const documentPath = pathParts.join('/');
    const trimedDocumentPath = documentPath
        .replace(/\/$/, '')
        .replace(/\s+/g, '')
        .replace(/%20/g, '');

    // e.g. /page/en/123/Applications/Submenu1/Article1
    // const route = `/page/${lang}/${document.id}/${documentPath}`;

    // This is temporary, not sure about the correct route yet.
    const route = `/${trimedDocumentPath}`;

    return route;
}

export {
    ApplicationsDocumentsProvider,
    buildDocumentRoute,
    useApplicationsDocuments,
    getDocumentById,
    findDocumentByTranslation,
    getDocumentParent,
};
export type { ApplicationsDocumentsContextValue };
